import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { AuthContext } from "~/auth/AuthProvider";
import { LanguagePreference } from "~/gql/graphqlBase.generated";
import logger from "~/helpers/logger";
import { useAppSelector } from "~/hooks/reduxHooks";
import {
	hasLocalLanguageChange,
	languageFromPreference,
	normalizeLanguage,
	writeCachedLanguage
} from "~/i18n/languagePreference";
import { useGetMyLanguagePreferenceQuery } from "~/services/endpoints/apiEndpoints";

const useSyncLanguagePreference = (): void => {
	const { i18n } = useTranslation();
	const { isCustomer, loggedIn } = useContext(AuthContext);
	const tenantId = useAppSelector((state) => state.settings.tenantId);
	const { data: myLanguagePreference } = useGetMyLanguagePreferenceQuery(undefined, {
		skip: !loggedIn || !tenantId || isCustomer
	});
	const serverPreference = myLanguagePreference?.me?.languagePreference;

	useEffect(() => {
		if (!serverPreference || hasLocalLanguageChange()) return;
		const language = languageFromPreference(serverPreference === LanguagePreference.Es ? "ES" : "EN");
		writeCachedLanguage(language);
		if (normalizeLanguage(i18n.resolvedLanguage ?? i18n.language) !== language) {
			i18n.changeLanguage(language).catch((error) => logger.warn("Language preference sync failed", error));
		}
	}, [serverPreference, i18n]);
};

export default useSyncLanguagePreference;
